import * as React from 'react';
import {
  GoogleMap,
  LoadScript,
  DirectionsService,
  DirectionsRenderer,
  OverlayViewF,
  Marker,
} from '@react-google-maps/api';
import warehouseIcon from 'assets/images/marker-warehouse.png';
import truckIcon from 'assets/images/markerTruck.png';
import clientIcon from 'assets/images/marker-client.png';
import clientCheckIcon from 'assets/images/marker-client-check.png';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';

import { useTripMapHelper } from './hooks';
import {
  OriginPositionCard,
  type OriginPositionCardRef,
  DestinationPositionCard,
  type DestinationPositionCardRef,
  TruckLastPositionCard,
  type TruckLastPositionCardRef,
} from './components';

type Props = React.ComponentProps<'div'>

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY_SINERGIA as string;
const MAP_ID = '87412a572f003751';

export const TripMap = ({ className, ...props }: Props) => {
  const {
    originAndDestinationDirectionOptions,
    handleOriginAndDestinationDirectionsServiceResponse,
    originAndDestinationDirections,
    originPosition,
    destinationPositions,
    mapDataIsReady,
    invoices,
    truckDirections,
    lastTruckPosition,
    truckDirectionOptions,
    handleTruckDirectionsServiceResponse,
  } = useTripMapHelper();

  const originPositionCardRef = React.useRef<OriginPositionCardRef>(null);
  const destinationPositionCardRefs = React.useRef<DestinationPositionCardRef[]>([]);
  const truckLastPositionCardRef = React.useRef<TruckLastPositionCardRef>(null);

  if (!mapDataIsReady) {
    return (
      <Skeleton {...props} className={cn('w-full h-[540px] rounded-md overflow-hidden', className)} />
    );
  }

  return (
    <div {...props} className={cn('w-full h-[540px] rounded-md overflow-hidden', className)}>
      <LoadScript googleMapsApiKey={API_KEY} mapIds={[MAP_ID]}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          zoom={10}
          clickableIcons={false}
          options={{ mapId: MAP_ID, gestureHandling: 'cooperative' }}
        >
          {originAndDestinationDirectionOptions && (
            <DirectionsService
              options={originAndDestinationDirectionOptions}
              callback={handleOriginAndDestinationDirectionsServiceResponse}
            />
          )}
          {originAndDestinationDirections && (
            <DirectionsRenderer options={{
              directions: originAndDestinationDirections,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#FFC100',
                strokeWeight: 8,
                strokeOpacity: 0.5,
              },
            }} />
          )}
          {originPosition && (
            <>
              <OverlayViewF mapPaneName={'floatPane'} position={originPosition}>
                <OriginPositionCard ref={originPositionCardRef} />
              </OverlayViewF>
              <Marker
                position={originPosition}
                icon={warehouseIcon}
                onClick={() => originPositionCardRef.current?.toggleOpen()}
              />
            </>
          )}
          {destinationPositions.map((position, i) => {
            const invoice = invoices[i]!;
            return (
              <React.Fragment key={i}>
                <OverlayViewF mapPaneName={'floatPane'} position={position}>
                  <DestinationPositionCard
                    invoice={invoice}
                    ref={(el) => (destinationPositionCardRefs.current[i] = el!)}
                  />
                </OverlayViewF>
                <Marker
                  position={position}
                  icon={invoice?.deliveryDate ? clientCheckIcon : clientIcon}
                  onClick={() => destinationPositionCardRefs.current[i]?.toggleOpen()}
                />
              </React.Fragment>
            );
          })}
          {truckDirectionOptions && (
            <DirectionsService
              options={truckDirectionOptions}
              callback={handleTruckDirectionsServiceResponse}
            />
          )}
          {truckDirections && (
            <DirectionsRenderer options={{
              directions: truckDirections,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#FF4D00',
                strokeWeight: 5,
                strokeOpacity: 0.7,
              },
            }} />
          )}
          {lastTruckPosition && (
            <>
              <OverlayViewF mapPaneName={'floatPane'} position={lastTruckPosition}>
                <TruckLastPositionCard ref={truckLastPositionCardRef} />
              </OverlayViewF>
              <Marker
                position={lastTruckPosition}
                icon={truckIcon}
                onClick={() => truckLastPositionCardRef.current?.toggleOpen()}
              />
            </>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};